// levels.js — structure detection for the trade engine (Phase 3a: FVG only)
// Candles assumed OLDEST -> NEWEST, object form {time,open,high,low,close,volume}.
// Caller is responsible for normalizing to that shape (see tradeEngine call site).

export function findFairValueGaps(candles, minGapPct = 0.1) {
  const gaps = [];
  for (let i = 1; i < candles.length - 1; i++) {
    const prev = candles[i - 1];
    const next = candles[i + 1];
    if (prev.high < next.low) {
      const size = ((next.low - prev.high) / prev.high) * 100;
      if (size >= minGapPct) {
        gaps.push({ type: "bullish", index: i, time: candles[i].time,
          bottom: prev.high, top: next.low, sizePct: size, filled: false });
      }
    }
    if (prev.low > next.high) {
      const size = ((prev.low - next.high) / next.high) * 100;
      if (size >= minGapPct) {
        gaps.push({ type: "bearish", index: i, time: candles[i].time,
          bottom: next.high, top: prev.low, sizePct: size, filled: false });
      }
    }
  }
  return markFilledGaps(gaps, candles);
}

export function markFilledGaps(gaps, candles) {
  return gaps.map((gap) => {
    for (let i = gap.index + 2; i < candles.length; i++) {
      const c = candles[i];
      if (c.low <= gap.bottom && c.high >= gap.top) {
        return { ...gap, filled: true, filledIndex: i };
      }
    }
    return gap;
  });
}

export function findSwingPoints(candles, lookback = 3) {
  const highs = [];
  const lows = [];

  for (let i = lookback; i < candles.length - lookback; i++) {
    const c = candles[i];
    let isSwingHigh = true;
    let isSwingLow = true;

    for (let j = 1; j <= lookback; j++) {
      if (candles[i - j].close >= c.close || candles[i + j].close > c.close) {
        isSwingHigh = false;
      }
      if (candles[i - j].close <= c.close || candles[i + j].close < c.close) {
        isSwingLow = false;
      }
    }

    if (isSwingHigh) highs.push({ index: i, price: c.close, time: c.time });
    if (isSwingLow) lows.push({ index: i, price: c.close, time: c.time });
  }

  return { highs, lows };
}

export function clusterLevels(pivots, tolerancePct = 2.5) {
  if (pivots.length === 0) return [];

  const sorted = [...pivots].sort((a, b) => a.price - b.price);
  const clusters = [];
  let current = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    // Anchor-relative merge: measure each pivot against the cluster's FIRST (anchor)
    // pivot, not the previous one, so a cluster can't drift cumulatively wider than
    // tolerancePct. 2.5% is eye-tuned (revertible) to match a real visual zone width.
    const anchor = current[0];
    const gapPct = (Math.abs(sorted[i].price - anchor.price) / anchor.price) * 100;
    if (gapPct <= tolerancePct) {
      current.push(sorted[i]);
    } else {
      clusters.push(current);
      current = [sorted[i]];
    }
  }
  clusters.push(current);

  return clusters.map((group) => {
    const prices = group.map((p) => p.price);
    return {
      priceLow: Math.min(...prices),
      priceHigh: Math.max(...prices),
      priceMid: prices.reduce((a, b) => a + b, 0) / prices.length,
      touches: group.length,
      lastTouchIndex: Math.max(...group.map((p) => p.index)),
      pivots: group,
    };
  });
}

export function scoreLevel(level, candles, opts = {}) {
  const {
    touchWeight = 40,
    recencyWeight = 35,
    volumeWeight = 25,
    maxTouches = 5,
  } = opts;

  const touchScore = Math.min(level.touches / maxTouches, 1) * touchWeight;

  const recency = level.lastTouchIndex / (candles.length - 1);
  const recencyScore = recency * recencyWeight;

  const avgVol =
    candles.reduce((s, c) => s + (c.volume || 0), 0) / (candles.length || 1);
  let zoneVol = 0;
  for (const c of candles) {
    if (c.high >= level.priceLow && c.low <= level.priceHigh) {
      zoneVol += c.volume || 0;
    }
  }
  const volRatio = avgVol > 0 ? zoneVol / (avgVol * level.touches || 1) : 0;
  const volumeScore = Math.min(volRatio, 1) * volumeWeight;

  return Math.round(touchScore + recencyScore + volumeScore);
}

export function getSupportResistance(candles, opts = {}) {
  const {
    lookback = 3,
    tolerancePct = 2.5,
    maxLevels = 5,
  } = opts;

  const { highs, lows } = findSwingPoints(candles, lookback);
  const currentPrice = candles[candles.length - 1].close;

  const resistanceZones = clusterLevels(highs, tolerancePct)
    .map((z) => ({ ...z, strength: scoreLevel(z, candles, opts) }))
    .filter((z) => z.priceMid > currentPrice)
    .sort((a, b) => b.strength - a.strength)
    .slice(0, maxLevels);

  const supportZones = clusterLevels(lows, tolerancePct)
    .map((z) => ({ ...z, strength: scoreLevel(z, candles, opts) }))
    .filter((z) => z.priceMid < currentPrice)
    .sort((a, b) => b.strength - a.strength)
    .slice(0, maxLevels);

  // Knob A — label-agnostic candidate pool for the L4 target pick. Zones from
  // BOTH pivot sources (swing highs AND swing lows), scored the SAME way as
  // support/resistance, NOT filtered by currentPrice here: tradeEngine applies
  // the overhead cut against entry (minTargetPrice), so levels.js is not the
  // authority on "above entry". Lets a flipped former-support sitting overhead
  // become a valid target. No dedupe: the consumer takes the nearest qualifying
  // level ([0] after sort), so duplicate zones at the same price are harmless;
  // "too close, take the next" is Knob B's job, not this build's.
  const levelsAbove = [
    ...clusterLevels(highs, tolerancePct),
    ...clusterLevels(lows, tolerancePct),
  ]
    .map((z) => ({ ...z, strength: scoreLevel(z, candles, opts) }))
    .sort((a, b) => a.priceLow - b.priceLow);

  // nearestResistanceAbove — the single trustworthy "nearest real wall above
  // entry". Built from the FRESH swing-HIGHS cluster pool, NOT from the
  // strength-sorted top-5 resistance[] above (that array can miss the true
  // nearest wall). Swing highs ONLY — overhead SUPPLY, not a target pool, so
  // no flipped-support / swing-low levels are merged in (that is B2's job).
  // Strictly above entry, unconsumed, nearest first; null when none qualifies.
  // Purely additive: support/resistance/levelsAbove are untouched.
  const nearestResistanceAbove =
    clusterLevels(highs, tolerancePct)
      .map((z) => ({ ...z, strength: scoreLevel(z, candles, opts) }))
      .filter((z) => z.priceLow > currentPrice)
      .filter((z) => isResistanceUnconsumed(z, candles))
      .sort((a, b) => a.priceLow - b.priceLow)[0] || null;

  // nearestSupportBelow — EXACT structural mirror of nearestResistanceAbove for the
  // support side. Built from the FRESH swing-LOWS cluster pool BEFORE the top-5
  // strength slice (support[] is strength-sorted, so support[0] is NOT nearest-below);
  // filtered below entry, unconsumed (isZoneUnconsumed — support-side helper), sorted
  // nearest-first (descending priceHigh). null when none qualifies. Additive: support/
  // resistance/levelsAbove/nearestResistanceAbove untouched.
  const nearestSupportBelow =
    clusterLevels(lows, tolerancePct)
      .map((z) => ({ ...z, strength: scoreLevel(z, candles, opts) }))
      .filter((z) => z.priceMid < currentPrice)
      .filter((z) => isZoneUnconsumed(z, candles))
      .sort((a, b) => b.priceHigh - a.priceHigh)[0] || null;

  return { currentPrice, support: supportZones, resistance: resistanceZones,
           levelsAbove, nearestResistanceAbove, nearestSupportBelow };
}

export function isZoneUnconsumed(zone, candles) {
  // A support zone is CONSUMED once price closes through it after the zone
  // formed: any candle AFTER zone.lastTouchIndex that CLOSES below
  // zone.priceLow (strict — a close below the bottom of the band, not a wick).
  // Returns true if NO such close exists (zone still valid as support),
  // false if the zone has been closed through.
  //
  // Ships dormant in Build 1 — exported, consumed by nothing yet (Build 2
  // wires the bounce path). Pure: no side effects, no state.
  //
  // Candle contract here is OBJECT form {time,open,high,low,close,volume}
  // (levels.js convention) — read close as c.close, never positional [4].
  if (!zone || !Array.isArray(candles)) return true;
  const start = zone.lastTouchIndex + 1;
  // Window is non-empty by construction for swing-derived zones (findSwingPoints
  // emits no pivot within `lookback` of the array end), but guard anyway: a zone
  // with no candles after formation cannot have been consumed.
  for (let i = start; i < candles.length; i++) {
    if (candles[i].close < zone.priceLow) {
      return false;
    }
  }
  return true;
}

export function isResistanceUnconsumed(zone, candles) {
  // A resistance zone is CONSUMED once price closes through it after the zone
  // formed: any candle AFTER zone.lastTouchIndex that CLOSES above
  // zone.priceHigh (strict — a close above the top of the band, not a wick).
  // Returns true if NO such close exists (zone still valid as resistance),
  // false if the zone has been closed through. Exact MIRROR of
  // isZoneUnconsumed (support: close below priceLow), inverted for overhead.
  //
  // Ships dormant — exported, consumed by nothing yet (no live caller). Pure:
  // no side effects, no state.
  //
  // Candle contract here is OBJECT form {time,open,high,low,close,volume}
  // (levels.js convention) — read close as c.close, never positional [4].
  if (!zone || !Array.isArray(candles)) return true;
  const start = zone.lastTouchIndex + 1;
  // Window is non-empty by construction for swing-derived zones (findSwingPoints
  // emits no pivot within `lookback` of the array end), but guard anyway: a zone
  // with no candles after formation cannot have been consumed.
  for (let i = start; i < candles.length; i++) {
    if (candles[i].close > zone.priceHigh) {
      return false;
    }
  }
  return true;
}
